import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { cn } from '@/lib/utils';

import { LocalImage } from '@/components/atoms/LocalImage';
import { Title } from '@/components/atoms/Typography/Title';
import { IconsLinksList } from '@/components/molecules/IconsLinksList';
import { NavigationLink } from '@/components/molecules/NavigationLink';

import ImageLink from '../../../../atoms/ImageLink/ImageLink';
import type { FooterConfigType } from '../../Footer.types';

interface FooterDesktopProps
    extends
        Omit<FooterConfigType, 'footerInfo' | 'promoCodesModal'>,
        HTMLAttributes<HTMLDivElement> {
    theme: string | undefined;
    location: string;
}

const FooterDesktop = forwardRef<HTMLDivElement, FooterDesktopProps>(
    ({ theme, links, location, className }, ref) => (
        <div
            ref={ref}
            className={cn(
                'container border-t border-grey-100 pb-12 pt-24',
                className,
            )}
        >
            <div className="flex w-full flex-row justify-between gap-x-6">
                {links.map((link, i) => (
                    <div
                        key={'desktop-footer-link-' + i}
                        className="w-[180px] xl:w-[220px]"
                        data-test-id={link['data-test-id']}
                    >
                        {link.logoIsVisible ? (
                            <LocalImage
                                src={
                                    theme === 'dark'
                                        ? '/images/logos/logo-white.svg'
                                        : '/images/logos/logo.svg'
                                }
                                alt="PayPo"
                                className="mb-6"
                                width={102}
                                height={28}
                            />
                        ) : (
                            <Title
                                size={'m'}
                                className="mb-6"
                            >
                                {link.name}
                            </Title>
                        )}

                        {!!link.menu?.list && (
                            <ul className="space-y-3">
                                {link.menu.list.map((listItem, i) => (
                                    <li key={'desktop-footer-menu-link-' + i}>
                                        <NavigationLink
                                            variant={{
                                                color: 'secondary',
                                                weight: 'normal',
                                                states: 'enable',
                                            }}
                                            scrollById={
                                                !!link.menu?.lead?.href &&
                                                link.menu.lead?.href ===
                                                    location
                                            }
                                            linkData={listItem}
                                            className="w-full text-left font-primary text-2xs"
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}

                        {link.socialLinks && (
                            <IconsLinksList
                                variant="horizontalLeft"
                                iconsData={link.socialLinks}
                                className="mt-6"
                            />
                        )}

                        {link.appLinks && (
                            <IconsLinksList
                                variant="vertical"
                                iconsData={link.appLinks}
                                className="mt-6 justify-start lg:items-start"
                            />
                        )}

                        {link.partnersLogo && (
                            <>
                                {link.partnersLogo.map((logo, i) => (
                                    <ImageLink
                                        key={`footer-partner-logo-${i}`}
                                        {...logo}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    ),
);

export default FooterDesktop;
